/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Tag, Table as TableAnt} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState} from 'react';

import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import equityAPI from '../../services/modules/IM/equityAPI';
import holding from '../../services/modules/JSON/holding';
import listingHoldingDetails from '../../services/modules/OM/listingHolding';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
// import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import mapAndFilter from '../../utils/mapCountry';
// import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  InvestmentKey,
  InvestmentLabel
} from '../Forms/Constants/Investment/InvestmentKeyLabel';
import {emissionUnits} from '../Forms/interface/unitChange';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {makeCheckKeyLabel} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import {isValueOrNot} from './Utils/filterReturn';
import TableTitleHeader from './Utils/TableTitleHeader';

const EquityTable: React.FC<{selectedYear: string}> = ({selectedYear}) => {
  interface DataType {
    _id: string;
    assetName: string;
    totalSpace: number;
    rentedFrom: string;
    rentedTo: string;
    totalEmission: number;
    cost: number;
    driven: number;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [showJobs, setShowJobs] = useState(false);
  const companyId = useCompanyStore.getState().companyId;
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);

  const holdingType = useJSONStore.getState().holdingType;

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      listingHoldingDetails(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`,
        'dataCollection'
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      listingHoldingDetails(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`,
        'dataCollection'
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([holding()])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    equityAPI(APITypeEnum.DELETE_ALL, {
      equityInvestment: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };
  const downVehicleKeyLabel = [
    {
      key: InvestmentKey.HOLDING_NAME,
      label: InvestmentLabel.HOLDING_NAME,
      type: objTypes.string
    },
    {
      key: InvestmentKey.HOLDING_TYPE,
      label: InvestmentLabel.HOLDING_TYPE,
      type: objTypes.json,
      format: holdingType
    },
    {
      key: InvestmentKey.EQUITY_SHARES,
      label: InvestmentLabel.EQUITY_SHARES,
      type: objTypes.percentage
    },
    {
      key: InvestmentKey.EMISSION,
      label: InvestmentLabel.EMISSION,
      type: objTypes.number
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = [
    {
      title: InvestmentLabel.HOLDING_NAME,
      dataIndex: InvestmentKey.HOLDING_NAME,
      ...SearchFilter(
        InvestmentLabel.HOLDING_NAME,
        InvestmentKey.HOLDING_NAME as keyof DataType
      )
    },
    {
      title: InvestmentLabel.HOLDING_TYPE,
      dataIndex: InvestmentKey.HOLDING_TYPE,
      editable: true,
      type: 'select',
      filterer: holdingType,
      filters: mapAndFilter(
        dataSource,
        holdingType,
        InvestmentKey.HOLDING_TYPE
      ).map((item) => {
        return {text: item.label, value: item.value};
      }),
      onFilter: (value, record) => {
        return record[InvestmentKey.HOLDING_TYPE as keyof DataType] === value;
      },
      render: (value) => {
        return (
          <>
            {holdingType.find((item) => {
              return item?.value === value;
            })?.label ?? '-'}
          </>
        );
      }
    },
    {
      title: InvestmentLabel.EQUITY_SHARES,
      type: 'number',
      dataIndex: InvestmentKey.EQUITY_SHARES,
      ...SearchFilter(
        InvestmentLabel.EQUITY_SHARES,
        InvestmentKey.EQUITY_SHARES as keyof DataType
      ),
      onFilter: (value, record: any) => {
        const recordValue = record[InvestmentKey.EQUITY_SHARES];

        return (
          (recordValue &&
            value &&
            recordValue
              .toString()
              .toLowerCase()
              .includes(value.toString().toLowerCase())) ??
          false
        );
      },
      editable: true,
      render: (value) => {
        return `${isValueOrNot(value)} ${value ? '%' : ''}`;
      }
    },

    {
      title: InvestmentLabel.EMISSION,
      dataIndex: InvestmentKey.EMISSION,
      editable: true,
      // ...sorter(upstreamKey.TOTAL_FUEL_EMISSIONS as keyof DataType),
      //   width: '100px',
      // filterer: monthlyYearly,            //   return {text: item.label, value: item.value};
      // }),
      // onFilter: (value, record) => {
      //   return record.totalEmission === value;
      // },
      render: (value: any) => {
        return `${formatIndian(value)} ${emissionUnits.T_CO2e}`;
      }
    },
    {
      title: makeCheckKeyLabel.STATUS.label,
      // dataIndex: 'status',
      editable: true,
      type: 'select',
      render: (value: any, record: any) => {
        const colorText = getStatusColor(statusEm.ACCEPTED);

        return (
          <Tag
            onClick={() => {
              // if (shouldBlockReadOnly()) {
              //   usePlatformStore
              //     .getState()
              //     .setOpenOnFreeTrialMessageModel(true);

              //   return;
              // }
              // setShowModelInfo(true);
              setEditData(record);
              setIsEdit(true);
            }}
            color={colorText.color}
            style={{
              fontWeight: 700
            }}
          >
            {colorText.text}
          </Tag>
        );
      }
    }
    // ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
    //   ? [
    //       {
    //         title: FullInfo.title,
    //         dataIndex: 'FullInfo',
    //         editable: true,
    //         type: 'select',
    //         render: (_: any, record: any) => {
    //           return (
    //             <Button
    //               onClick={() => {
    //                 setShowModelInfo(true);
    //                 setEditData(record);
    //                 setIsEdit(true);
    //               }}
    //               shape="circle"
    //               style={{
    //                 marginRight: 16,
    //                 cursor: 'pointer',
    //                 color: '#435B37',
    //                 background: '#F5FBF3',
    //                 border: 0
    //               }}
    //             >
    //               <ExclamationOutlined />
    //             </Button>
    //           );
    //         }
    //       }
    //     ]
    //   : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Equity Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel: undefined,
          setIsEdit: undefined,
          buttonTitle: ''
        }}
        sampleDownload={{href: SampleExcel.InvestmentEquity}}
        jobs={{
          setShowJobs: null,
          uploadType: ''
        }}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        onlyYear={true}
        onlyUpload={true}
        showAddOnly={true}
        tooltip={InformativeText.InvestInfo.equityMain}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        // rowSelection={{
        //   type: 'checkbox',
        //   onChange: (selectedKey: React.Key[], selectedRow: DataType[]) => {
        //     setSelectedRowKeys(
        //       selectedRow.map((item) => {
        //         return item._id;
        //       })
        //     );
        //   },
        //   selectedRowKeys
        // }}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.InvestmentEquity}
        details={`${selectedYear}`}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={downVehicleKeyLabel} />
        }
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        formPreview={downVehicleKeyLabel}
      />
    </div>
  );
};

export default EquityTable;
